import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { seoAPI } from '../../services/api';

interface Props {
  assetId: number;
  children: React.ReactNode;
}

const SeoKeywordGate: React.FC<Props> = ({ assetId, children }) => {
  const [checking, setChecking] = useState(true);
  const [hasKeywords, setHasKeywords] = useState(false);

  // Modal state
  const [keywords, setKeywords] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    seoAPI.getKeywords(assetId)
      .then(res => {
        const kws: any[] = res.data.data.keywords || [];
        setHasKeywords(kws.length > 0);
      })
      .catch(() => { /* show gate on error */ })
      .finally(() => setChecking(false));
  }, [assetId]);

  const addKeyword = () => {
    const kw = input.trim();
    if (!kw || keywords.includes(kw)) return;
    setKeywords(p => [...p, kw]);
    setInput('');
  };

  const handleAutoExtract = async () => {
    setExtracting(true);
    try {
      const res = await seoAPI.autoExtractKeywords(assetId);
      const suggestions: string[] = res.data.data.suggestions || [];
      if (suggestions.length === 0) {
        toast('No keywords found — add them manually.');
        return;
      }
      setKeywords(prev =>
        [...prev, ...suggestions]
          .filter((v, i, a) => a.indexOf(v) === i)
          .slice(0, 20)
      );
      toast.success(`${suggestions.length} keywords extracted`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Auto-extract failed — add keywords manually');
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    if (keywords.length === 0) return;
    setSaving(true);
    try {
      await seoAPI.saveKeywords(assetId, keywords);
      setHasKeywords(true);
    } catch {
      toast.error('Failed to save keywords');
    } finally {
      setSaving(false);
    }
  };

  if (checking) {
    return (
      <div className="text-center py-5">
        <span className="spinner-border text-primary"></span>
      </div>
    );
  }

  if (!hasKeywords) {
    return (
      <>
        {/* Render page behind — blurred */}
        <div style={{ filter: 'blur(3px)', pointerEvents: 'none', userSelect: 'none' }}>
          {children}
        </div>

        {/* Blocking overlay */}
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(15, 23, 42, 0.65)',
          zIndex: 1060,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}>
          <div style={{
            background: '#fff', borderRadius: 18, padding: '2rem',
            maxWidth: 520, width: '100%',
            boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
          }}>
            {/* Icon + heading */}
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{
                width: 64, height: 64,
                background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                borderRadius: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 1rem',
                boxShadow: '0 8px 20px rgba(6,182,212,0.35)',
              }}>
                <i className="bi bi-key-fill" style={{ fontSize: 26, color: '#fff' }}></i>
              </div>
              <h5 style={{ fontWeight: 800, marginBottom: '0.4rem', color: '#0f172a' }}>
                Set Your Target Keywords
              </h5>
              <p style={{ color: '#64748b', fontSize: '0.88rem', margin: 0, lineHeight: 1.5 }}>
                Keywords drive your SEO scoring, AI suggestions, and rank tracking.
                Add at least one before you start.
              </p>
            </div>

            {/* Keyword tags */}
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 6,
              marginBottom: '0.75rem', minHeight: 36,
              padding: '8px 12px',
              background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0',
            }}>
              {keywords.length === 0
                ? <span style={{ color: '#94a3b8', fontSize: '0.82rem', alignSelf: 'center' }}>
                    No keywords yet — type below or auto-extract
                  </span>
                : keywords.map(kw => (
                  <span key={kw} style={{
                    background: '#eff6ff', color: '#2563eb',
                    border: '1px solid #bfdbfe',
                    borderRadius: 20, padding: '3px 10px',
                    fontSize: '0.8rem',
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                  }}>
                    {kw}
                    <button
                      onClick={() => setKeywords(p => p.filter(k => k !== kw))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#93c5fd', padding: 0, lineHeight: 1, fontSize: '1rem' }}
                    >×</button>
                  </span>
                ))
              }
            </div>

            {/* Input row */}
            <div style={{ display: 'flex', gap: 8, marginBottom: '0.75rem' }}>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder='e.g. "Best IT company in Bahrain"'
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                style={{ borderRadius: 8 }}
              />
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={addKeyword}
                disabled={!input.trim()}
                style={{ whiteSpace: 'nowrap', borderRadius: 8 }}
              >
                Add
              </button>
            </div>

            {/* Auto-extract */}
            <button
              className="btn btn-outline-secondary btn-sm w-100 mb-4"
              onClick={handleAutoExtract}
              disabled={extracting}
              style={{ borderRadius: 8 }}
            >
              {extracting
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Extracting…</>
                : <><i className="bi bi-magic me-1"></i>Auto-Extract Keywords from My Site</>}
            </button>

            {/* Save button */}
            <button
              className="btn btn-primary w-100"
              disabled={keywords.length === 0 || saving}
              onClick={handleSave}
              style={{ borderRadius: 10, padding: '0.65rem', fontWeight: 600 }}
            >
              {saving
                ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving…</>
                : keywords.length > 0
                  ? `Start with ${keywords.length} keyword${keywords.length > 1 ? 's' : ''} →`
                  : 'Add at least one keyword to continue'}
            </button>
          </div>
        </div>
      </>
    );
  }

  return <>{children}</>;
};

export default SeoKeywordGate;
